import {
  DataTypes,
  type Animation,
  type AnimationGroup,
  type DieType,
  type Manifest,
  type MaterialResponse,
  type RandomTrigger,
  type Trigger,
} from "@/types";

const hasEntries = (value: unknown) =>
  value !== undefined &&
  value !== null &&
  typeof value === "object" &&
  Object.keys(value).length > 0;

const hasArrayEntries = (value: unknown) =>
  Array.isArray(value) && value.length > 0;

const isSupportedMaterial = (
  material: MaterialResponse | string | undefined,
): material is MaterialResponse => {
  if (!material || typeof material === "string") {
    return false;
  }

  // Skip sets with custom shaders or materials
  if (material.shader || material.nodeJson || material.snippets?.length) {
    return false;
  }

  return true;
};

const isInstanceableTargetedAnimation = ({
  property,
  dataType,
}: Animation): boolean => {
  if (property === "scaling" && dataType === DataTypes.VECTOR) return true;
  if (property === "visibility" && dataType === DataTypes.NUMBER) return true;
  return false;
};

// Currently allowing dieRemoved animations for scaling and visibility
const isInstanceableTrigger = (
  trigger: Trigger | RandomTrigger,
  animationGroups: Record<string, AnimationGroup> | undefined,
): boolean => {
  if (Array.isArray(trigger)) return false;
  if (trigger.triggerStart !== "dieRemoved") return false;
  if (trigger.loop) return false;

  const group = animationGroups?.[trigger.definition];
  if (!group || !hasArrayEntries(group.targetedAnimations)) return false;

  return group.targetedAnimations.every(({ animation }) =>
    isInstanceableTargetedAnimation(animation),
  );
};

const areNodeAnimationsInstanceable = (
  animations: (Trigger | RandomTrigger)[] | undefined,
  animationGroups: Record<string, AnimationGroup> | undefined,
): boolean => {
  if (!hasArrayEntries(animations)) return true;
  return animations!.every((trigger) =>
    isInstanceableTrigger(trigger, animationGroups),
  );
};

export const getInstancingKey = ({
  dieType,
  manifest,
  setId,
}: {
  dieType: DieType;
  manifest: Manifest | undefined;
  setId: string;
}) => {
  const node = manifest?.[dieType];

  if (!manifest || !node) return;

  // Skip sets with top level particle systems or custom shaders. Top-level
  // animationGroups are allowed when the node-level triggers reference only
  // instanceable shapes (the node walk below is the source of truth).
  if (hasEntries(manifest.particleSystems) || hasEntries(manifest.shaders)) {
    return;
  }

  // Skip sets with nodes that have particle systems or sibling nodes
  if (
    hasArrayEntries(node.particleSystems) ||
    hasArrayEntries(node.siblingNodes)
  ) {
    return;
  }

  // Allow node animations only if every trigger resolves to a scale-out style
  // dieRemoved animation we can drive per-instance via the manifest sampler.
  if (!areNodeAnimationsInstanceable(node.animations, manifest.animationGroups))
    return;

  const materialEntries = Object.entries(node.materials ?? {});

  // Skip sets with multiple materials which need instancing per mesh/material combination
  if (materialEntries.length !== 1) return;

  const [[materialName, material]] = materialEntries;
  if (!isSupportedMaterial(material)) return;

  const modelPath = manifest.resources.models[node.modelFile];
  if (!modelPath) return;

  return `${setId}:${manifest.slug}:${dieType}:${node.modelFile}:${modelPath}:${materialName}`;
};
